import React, { useState, useEffect } from 'react';
import { connect } from 'react-redux';
import Style from './Governance.module.css';
import AddEditRule from './AddEditRule';
import ConfirmPopup from '../common/popover/confirmPopup';
import { getScanRules } from '../../store/entities/dataControlEntity';

const ACTION_BADGE = {
  IGNORE: { label: 'Ignore',     cls: 'neutral' },
  REPORT: { label: 'Report',     cls: 'low' },
  WARN:   { label: 'Warn User',  cls: 'moderate' },
  BLOCK:  { label: 'Block User', cls: 'critical' },
};
const CATEGORY_LABEL = {
  FINANCIAL:     'Financial',
  GOVERNMENT_ID: 'Government IDs',
  CREDENTIALS:   'Credentials & Secrets',
  PII:           'PII',
  CUSTOM:        'Custom',
};

const ActionBadge = ({ code }) => {
  const a = ACTION_BADGE[code] || { label: code, cls: 'neutral' };
  return <span className={`${Style.badge} ${Style[a.cls]}`}>{a.label}</span>;
};

// E1: Enabled/Disabled badge for read-only view.
const EnabledBadge = ({ isActive }) =>
  isActive
    ? <span className={`${Style.badge} ${Style.low}`}>Enabled</span>
    : <span className={`${Style.badge} ${Style.neutral}`}>Disabled</span>;

// ─────────────────────────────────────────────────────────────────────────────
// DataControls — renders the rule tables and delegates all mutations upward.
//
// The rule list is ALWAYS supplied by the parent via `props.rules` — this
// component never reads the policy from Redux itself. Each caller owns its own
// single source of truth:
//   • GovernanceEditor (editable)     → local staged rules
//   • Governance landing (read-only)  → the live PUBLISHED policy's rules
//
// When editable, the parent also provides:
//   props.onSaveRule    — called with (payload, existingRule|null) on Add/Edit
//   props.onDeleteRule  — called with (rule) on Delete
// ─────────────────────────────────────────────────────────────────────────────
const DataControls = (props) => {
  const { editable, scanRules } = props;
  const [modal, setModal] = useState(null);          // { family, rule }
  const [confirmDelete, setConfirmDelete] = useState(null);

  // The FILE_TRANSFER "Detection Rule" column and the Add/Edit modal both resolve
  // scan-rule names from this catalogue. Load it here (idempotent) so BOTH
  // callers — the read-only landing AND the editor — have names available without
  // depending on an Add/Edit modal having been opened first in the session.
  useEffect(() => {
    if (!scanRules || scanRules.length === 0) props.getScanRules();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const scanRuleNameById = new Map((scanRules || []).map((sr) => [sr.scanRuleId, sr.name]));

  const rules = Array.isArray(props.rules) ? props.rules : [];

  const sensitive    = rules.filter((r) => r.ruleFamilyCode === 'SENSITIVE_DATA');
  const fileTransfer = rules.filter((r) => r.ruleFamilyCode === 'FILE_TRANSFER');
  const defaultCount = sensitive.filter((r) => r.sourceCode === 'DEFAULT').length;
  const customCount  = sensitive.filter((r) => r.sourceCode !== 'DEFAULT').length;

  // ── handlers ────────────────────────────────────────────────────────────────
  const handleSave = (payload, existingRule) => {
    setModal(null);
    if (props.onSaveRule) props.onSaveRule(payload, existingRule ?? null);
  };

  const handleDeleteConfirmed = () => {
    const rule = confirmDelete;
    setConfirmDelete(null);
    if (props.onDeleteRule) props.onDeleteRule(rule);
  };

  // E2: inline toggle — no modal needed, fires onSaveRule directly.
  const handleToggleEnabled = (rule) => {
    if (props.onSaveRule) props.onSaveRule({ isActive: !rule.isActive }, rule);
  };

  // ── grouped sensitive rules (by category) ──────────────────────────────────
  const orderedCats = ['FINANCIAL', 'GOVERNMENT_ID', 'CREDENTIALS', 'PII', 'CUSTOM'];
  const grouped = orderedCats
    .map((cat) => ({ cat, items: sensitive.filter((r) => (r.categoryCode || 'CUSTOM') === cat) }))
    .filter((g) => g.items.length > 0);

  // E1: +1 column for Enabled; editable adds the Actions column on top.
  const colsSensitive = editable ? 6 : 5;
  const colsFile      = editable ? 5 : 4;

  const isPending = (r) => r._tempId !== undefined || r._dirty;

  return (
    // width:100% so the content fills the LoaderWrapper's centering flexbox
    // (isFlex) instead of shrinking to content and leaving left/right gutters.
    <div style={{ width: '100%' }}>
      <div className={Style.statRow}>
        <div className={Style.statCard}>
          <div className={Style.statLabel}>Sensitive Data Detection Rules</div>
          <div className={Style.statValue}>{sensitive.length}</div>
          <div className={Style.statNote}>{defaultCount} default, {customCount} custom</div>
        </div>
        <div className={Style.statCard}>
          <div className={Style.statLabel}>File Transfer Rules</div>
          <div className={Style.statValue}>{fileTransfer.length}</div>
          <div className={Style.statNote}>Upload, download, and filename restrictions</div>
        </div>
      </div>

      {/* ── Sensitive Data Detection ── */}
      <div className={Style.card}>
        <div className={Style.cardHeader}>
          <div className={Style.cardTitle}>Sensitive Data Detection</div>
          <div className={Style.cardSubtitle}>
            Global detection rules for sensitive content — detection comes from the linked scan rule
          </div>
        </div>
        <div className={Style.cardBody}>
          <table className={Style.table}>
            <thead>
              <tr>
                <th>Category</th><th>Detection Rule</th><th>Source</th><th>Action</th>
                <th>Enabled</th>
                {editable && <th>Actions</th>}
              </tr>
            </thead>
            <tbody>
              {grouped.length === 0 && (
                <tr>
                  <td colSpan={colsSensitive} style={{ textAlign: 'center', padding: '24px' }}>
                    No detection rules
                  </td>
                </tr>
              )}
              {grouped.map((g) =>
                g.items.map((r, idx) => (
                  <tr key={r.ruleId ?? r._tempId}>
                    {idx === 0 && (
                      <td className={Style.categoryCell} rowSpan={g.items.length}>
                        {CATEGORY_LABEL[g.cat] || g.cat}
                      </td>
                    )}
                    <td>
                      {r.ruleDescription}
                      {isPending(r) && (
                        <span className={`${Style.badge} ${Style.moderate}`} style={{ marginLeft: 8, fontSize: 11 }}>
                          Unsaved
                        </span>
                      )}
                    </td>
                    <td>
                      {r.sourceCode === 'DEFAULT'
                        ? <span className={`${Style.badge} ${Style.neutral}`}>Default</span>
                        : <span className={`${Style.badge} ${Style.primaryBadge}`}>Custom</span>}
                    </td>
                    <td><ActionBadge code={r.actionCode} /></td>
                    {/* E1/E2: Enabled column — inline toggle. */}
                    <td>
                      {editable
                        ? (
                          <input
                            type="checkbox"
                            checked={!!r.isActive}
                            onChange={() => handleToggleEnabled(r)}
                            style={{ cursor: 'pointer' }}
                          />
                        )
                        : <EnabledBadge isActive={!!r.isActive} />}
                    </td>
                    {/* DEFAULT rows: the definition is locked inside the modal;
                        Delete is never offered (API rejects it with ERR_GOV_006). */}
                    {editable && (
                      <td>
                        <button className={Style.btn}
                          onClick={() => setModal({ family: 'SENSITIVE_DATA', rule: r })}>
                          Edit
                        </button>
                        {r.sourceCode !== 'DEFAULT' && (
                          <button className={Style.btn} onClick={() => setConfirmDelete(r)}>
                            Delete
                          </button>
                        )}
                      </td>
                    )}
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
        {editable && (
          <div className={Style.cardFooter}>
            <button className={`${Style.btn} ${Style.btnPrimary}`}
              onClick={() => setModal({ family: 'SENSITIVE_DATA', rule: null })}>
              Add Detection Rule
            </button>
          </div>
        )}
      </div>

      {/* ── File Transfer Controls ── */}
      <div className={Style.card}>
        <div className={Style.cardHeader}>
          <div className={Style.cardTitle}>File Transfer Controls</div>
          <div className={Style.cardSubtitle}>
            Restrict file uploads and downloads by size, type, and filename pattern
          </div>
        </div>
        <div className={Style.cardBody}>
          <table className={Style.table}>
            <thead>
              <tr>
                <th>Rule Name</th><th>Detection Rule</th><th>Action</th>
                <th>Enabled</th>
                {editable && <th>Actions</th>}
              </tr>
            </thead>
            <tbody>
              {fileTransfer.length === 0 && (
                <tr>
                  <td colSpan={colsFile} style={{ textAlign: 'center', padding: '24px' }}>
                    No file transfer rules
                  </td>
                </tr>
              )}
              {fileTransfer.map((r) => (
                <tr key={r.ruleId ?? r._tempId}>
                  <td>
                    <strong>{r.ruleDescription}</strong>
                    {isPending(r) && (
                      <span className={`${Style.badge} ${Style.moderate}`} style={{ marginLeft: 8, fontSize: 11 }}>
                        Unsaved
                      </span>
                    )}
                  </td>
                  <td>{scanRuleNameById.get(r.scanRuleId) || r.scanRuleId || '—'}</td>
                  <td><ActionBadge code={r.actionCode} /></td>
                  {/* E1/E2: Enabled column */}
                  <td>
                    {editable
                      ? (
                        <input
                          type="checkbox"
                          checked={!!r.isActive}
                          onChange={() => handleToggleEnabled(r)}
                          style={{ cursor: 'pointer' }}
                        />
                      )
                      : <EnabledBadge isActive={!!r.isActive} />}
                  </td>
                  {editable && (
                    <td>
                      <button className={Style.btn}
                        onClick={() => setModal({ family: 'FILE_TRANSFER', rule: r })}>
                        Edit
                      </button>
                      <button className={Style.btn} onClick={() => setConfirmDelete(r)}>
                        Delete
                      </button>
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {editable && (
          <div className={Style.cardFooter}>
            <button className={`${Style.btn} ${Style.btnPrimary}`}
              onClick={() => setModal({ family: 'FILE_TRANSFER', rule: null })}>
              Add Transfer Rule
            </button>
          </div>
        )}
      </div>

      {modal && (
        <AddEditRule
          family={modal.family}
          rule={modal.rule}
          saving={false}
          onSave={handleSave}
          onClose={() => setModal(null)}
        />
      )}

      {confirmDelete && (
        <ConfirmPopup
          details={{
            icon: '', type: 'warning', title: 'Remove Rule?',
            description: `Remove "${confirmDelete.ruleDescription}" from the draft? This is a local change — it won't be saved until you click Save Draft.`,
            buttons: 'Y,N',
          }}
          onYesClick={handleDeleteConfirmed}
          onNoClick={() => setConfirmDelete(null)}
        />
      )}
    </div>
  );
};

const mapStateToProps = (state) => ({
  // The rule list is passed in via props.rules by every caller; only scanRules
  // (for the FILE_TRANSFER detection-rule name lookup + the Add/Edit modal) is
  // read from Redux here.
  scanRules: state.entities.dataControlEntity.scanRules,
});

const mapDispatchToProps = (dispatch) => ({
  getScanRules: () => dispatch(getScanRules()),
});

export default connect(mapStateToProps, mapDispatchToProps)(DataControls);
